import React, { useState, useEffect, useCallback } from 'react';
import LoadingPage from './maincontent/loading/LoadingPage';
import JapanTopPage from './maincontent/japan-top/JapanTopPage';
import EnergeticCityPage from './maincontent/energetic-city/EnergeticCityPage';
import GradientPage from './maincontent/gradient-page/GradientPage';
import NewLightStarPage from './maincontent/new-light-star/NewLightStarPage';
import TheWorldPage from './maincontent/the-world/TheWorldPage';
import { useScrollControl } from './hooks/useScrollControl';
import './styles/globals.css';
import './App.css';

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [isScrollLocked, setIsScrollLocked] = useState(false);
  
  const {
    currentPage: scrollCurrentPage,
    scrollPosition,
    isSnapping,
    lockScroll,
    unlockScroll
  } = useScrollControl({
    smoothScrollSpeed: 1.5,
    pageHeight: window.innerHeight,
    snapThreshold: 0.1,
    totalPages: 5
  });

  // useScrollControlからの現在ページを使用
  useEffect(() => {
    if (!isLoading) {
      setCurrentPage(scrollCurrentPage);
    }
  }, [scrollCurrentPage, isLoading]);

  // TheWorldPage（ページ4）に到達したら自動的にスクロールロック
  useEffect(() => {
    if (!isLoading && currentPage === 4) {
      setIsScrollLocked(true);
      lockScroll();
      console.log('ザ・ワールドページに到達：スクロールロック開始');
    }
  }, [currentPage, isLoading, lockScroll]);

  // ローディング完了のハンドラー
  const handleLoadingComplete = useCallback(() => {
    setIsLoading(false);
    setCurrentPage(0); // Japan Top Page
  }, []);

  // スクロールロック状態の変更ハンドラー
  const handleScrollLockChange = useCallback((locked: boolean) => {
    setIsScrollLocked(locked);
    if (locked) {
      lockScroll();
    } else {
      unlockScroll();
    }
  }, [lockScroll, unlockScroll]);

  // セキュリティ対策: 右クリック禁止とコンソール警告
  useEffect(() => {
    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      // F12, Ctrl+Shift+I, Ctrl+U などの開発者ツール系のキーを無効化
      if (
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && e.key === 'I') ||
        (e.ctrlKey && e.key === 'u')
      ) {
        e.preventDefault();
      }
    };

    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown);

    // コンソール警告
    console.warn('🚨 警告: このサイトは保護されています。不正な操作は禁止されています。');

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // スムーズスクロールの設定
  useEffect(() => {
    document.documentElement.style.scrollBehavior = 'smooth';
    return () => {
      document.documentElement.style.scrollBehavior = 'auto';
    };
  }, []);

  return (
    <div className="App">
      {/* ローディング画面 */}
      {isLoading && (
        <LoadingPage 
          onLoadingComplete={handleLoadingComplete}
          loadingDuration={2000}
        />
      )}

      {/* メインコンテンツ */}
      {!isLoading && (
        <div className="main-content">
          {/* Japan Top Page */}
          <section className="page-section">
            <JapanTopPage isVisible={currentPage === 0} />
          </section>

          {/* Energetic City Page */}
          <section className="page-section">
            <EnergeticCityPage isVisible={currentPage === 1} />
          </section>

          {/* Gradient Page */}
          <section className="page-section">
            <GradientPage isVisible={currentPage === 2} />
          </section>

          {/* New Light Star Page */}
          <section className="page-section">
            <NewLightStarPage 
              isVisible={currentPage === 3}
              scrollProgress={Math.max(0, Math.min(1, (scrollPosition - window.innerHeight * 3) / window.innerHeight))}
            />
          </section>

          {/* The World Page */}
          <section className="page-section">
            <TheWorldPage 
              isVisible={currentPage >= 4}
              onScrollLockChange={handleScrollLockChange}
            />
          </section>
        </div>
      )}

      {/* デバッグ情報（開発時のみ） */}
      {process.env.NODE_ENV === 'development' && (
        <div className="debug-info">
          <p>Page: {currentPage} (Scroll: {scrollCurrentPage})</p>
          <p>Scroll: {Math.round(scrollPosition)}</p>
          <p>Locked: {isScrollLocked ? 'Yes' : 'No'}</p>
          <p>Snapping: {isSnapping ? 'Yes' : 'No'}</p>
        </div>
      )}
    </div>
  );
}

export default App;
